import { useRefreshAskHelenaConversationMessages } from "controller";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { debugConsole } from "utilz/helpers";
import { trackAiHome } from "../../analytics";
import {
  askHelenaInterruptedTurn,
  clearAskHelenaInterruptedTurn,
  isAskHelenaResumable,
  rehydrateAskHelenaConversation,
} from "../store";
import {
  isAskHelenaThreadSettled,
  toAskHelenaMessage,
} from "../utils/toAskHelenaMessage";

const RESUME_RETRY_DELAYS_MS = [2000, 4000, 8000, 16000];

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

interface RetryLadderOutcome {
  recovered: boolean;
  attempts: number;
  isCancelled: boolean;
}

const runRetryLadder = async ({
  attempt,
  isCancelled,
  delays,
}: {
  attempt: () => Promise<boolean>;
  isCancelled: () => boolean;
  delays: number[];
}): Promise<RetryLadderOutcome> => {
  let attempts = 0;

  for (;;) {
    attempts += 1;
    if (await attempt()) {
      return { recovered: true, attempts, isCancelled: false };
    }
    if (isCancelled()) {
      return { recovered: false, attempts, isCancelled: true };
    }

    const nextDelay = delays[attempts - 1];
    if (nextDelay === undefined) {
      return { recovered: false, attempts, isCancelled: false };
    }

    await delay(nextDelay);
    if (isCancelled()) {
      return { recovered: false, attempts, isCancelled: true };
    }
  }
};

interface UseAskHelenaTurnResumeReturn {
  isResuming: boolean;
  resume: (() => void) | null;
}

export const useAskHelenaTurnResume = (): UseAskHelenaTurnResumeReturn => {
  const interruptedTurn = useAtomValue(askHelenaInterruptedTurn);
  const canResume = useAtomValue(isAskHelenaResumable);
  const rehydrate = useSetAtom(rehydrateAskHelenaConversation);
  const clearInterruptedTurn = useSetAtom(clearAskHelenaInterruptedTurn);
  const refreshMessages = useRefreshAskHelenaConversationMessages();

  const [isResuming, setIsResuming] = useState(false);
  const claimedRef = useRef<string | null>(null);
  const runIdRef = useRef(0);
  const interruptedRef = useRef(interruptedTurn);

  const conversationId = interruptedTurn?.conversationId ?? null;
  const interruptedAt = interruptedTurn?.interruptedAt ?? 0;

  useEffect(() => {
    interruptedRef.current = interruptedTurn;
  }, [interruptedTurn]);

  useEffect(
    () => () => {
      runIdRef.current += 1;
    },
    [],
  );

  const readStoredThread = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const stored = await refreshMessages(id);
        return rehydrate({
          conversationId: id,
          messages: stored.map(toAskHelenaMessage),
          isSettled: isAskHelenaThreadSettled(stored),
        });
      } catch (error: unknown) {
        debugConsole(
          "Ask Helena: could not recover the interrupted turn",
          error,
        );
        return false;
      }
    },
    [refreshMessages, rehydrate],
  );

  const runResume = useCallback(
    async (isManual: boolean) => {
      if (!conversationId) return;

      runIdRef.current += 1;
      const thisRun = runIdRef.current;
      setIsResuming(true);

      try {
        const outcome = await runRetryLadder({
          attempt: () => readStoredThread(conversationId),
          isCancelled: () =>
            runIdRef.current !== thisRun || !interruptedRef.current,
          delays: isManual ? [] : RESUME_RETRY_DELAYS_MS,
        });

        if (outcome.recovered) clearInterruptedTurn();
        if (outcome.isCancelled) return;

        trackAiHome("ai_turn_resumed", {
          chat_id: conversationId,
          method: outcome.recovered ? "refetch" : "abandoned",
          gap_ms: Date.now() - interruptedAt,
          recovered: outcome.recovered,
          attempts: outcome.attempts,
          is_manual: isManual,
        });
      } finally {
        if (runIdRef.current === thisRun) setIsResuming(false);
      }
    },
    [clearInterruptedTurn, conversationId, interruptedAt, readStoredThread],
  );

  useEffect(() => {
    if (!interruptedTurn) {
      claimedRef.current = null;
      return;
    }

    if (interruptedTurn.conversationId === null) {
      clearInterruptedTurn();
      return;
    }

    if (!canResume) return;

    const claim = `${interruptedTurn.sessionId}:${interruptedTurn.assistantMessageId}`;
    if (claimedRef.current === claim) return;
    claimedRef.current = claim;

    void runResume(false);
  }, [canResume, clearInterruptedTurn, interruptedTurn, runResume]);

  const resume = useCallback(() => {
    void runResume(true);
  }, [runResume]);

  return { isResuming, resume: conversationId ? resume : null };
};
